import type { HTTPStatusCode } from '~/api/httpStatus'
import { HTTPStatus, HTTPStatusText } from '~/api/httpStatus'
import { formatBytes } from '~/cynqueModules/formatBytes'

export interface IServiceError {
  message: string
  error?: IServiceError
  statusCode: number | null
}

export class ServiceError extends Error implements IServiceError {
  statusCode: number | null

  constructor(statusCode: number | null, message: string) {
    super(message)
    this.statusCode = statusCode
  }
}

export class UserAuthenticationError extends ServiceError {
  // Added statusCode, cause we have more than one statusCode that can result
  // in wanting to return a UserAuthenticationError, and wanted to be able
  // to include statusCode in displayed error message.  JR.
  constructor(statusCode: number, message: string) {
    super(statusCode, message)
  }
}

/**
 * A passkey route refused the request. `problemType` is the api's RFC 9457
 * `type` (see PASSKEY_PROBLEM_TYPES in types/passkey/models.ts), so a caller
 * can branch on it; `message` is already fit to show the user.
 */
export class PasskeyError extends ServiceError {
  problemType: string | undefined

  constructor(
    statusCode: number,
    problemType: string | undefined,
    message: string
  ) {
    super(statusCode, message)
    this.name = 'PasskeyError'
    this.problemType = problemType
  }
}

/**
 * Where passkeys work, from the RP ID the api sent: the deployment's webapp
 * host (`host(WEBAPP_ORIGIN)` on the api). Undefined when there is nowhere to
 * send anyone -- no RP ID, or a review app's, which is under the reserved
 * `.invalid` TLD (RFC 2606) precisely so that no page can be at it.
 */
export function passkeyHostFromRpId(
  rpId: string | undefined
): string | undefined {
  if (!rpId || /\.invalid$/i.test(rpId)) {
    return undefined
  }
  return rpId.toLowerCase()
}

/**
 * This page's host is not covered by the deployment's RP ID, so no passkey
 * ceremony can succeed here: the app opened at its Heroku address instead of
 * its own (staging.cynque.app, wxtg.cynque.app), a review app (whose api
 * answers with `passkeys-unavailable.invalid`), or local dev pointed at the
 * staging api. Thrown before the browser is asked, so the login page can hide
 * passkeys instead of showing a button that always fails.
 *
 * The message names the address passkeys DO work at, when there is one: on a
 * Heroku address the bare "not available here" read as passkeys being broken.
 */
export class PasskeysUnavailableHereError extends PasskeyError {
  /** Where passkeys work instead, or undefined when nowhere does. */
  readonly passkeyHost: string | undefined

  constructor(rpId?: string) {
    const passkeyHost = passkeyHostFromRpId(rpId)
    super(
      0,
      undefined,
      passkeyHost
        ? `Passkeys work at ${passkeyHost}. Open Cynque there to use them.`
        : "Passkeys aren't available at this address."
    )
    this.name = 'PasskeysUnavailableHereError'
    this.passkeyHost = passkeyHost
  }
}

export class AsyncOperationError extends ServiceError {
  errorType: string
  operationId: string
  details?: any

  constructor(
    message: string,
    errorType: string,
    operationId: string,
    details?: any
  ) {
    super(null, message)
    this.errorType = errorType
    this.operationId = operationId
    this.details = details
  }
}

/**
 * A transport-level failure: the request never produced an HTTP response at all.
 *
 * `fetch` REJECTS (rather than resolving with a status) when the request cannot
 * be made or completed — offline, DNS failure, connection reset, TLS failure,
 * CORS rejection, a dyno restarting mid-flight, or the tab being suspended. The
 * rejection value is a bare `TypeError`, worded per engine: "Failed to fetch"
 * (V8) / "Load failed" (WebKit). Every http verb in `services/http/` documented
 * this in a comment — "fetch throws network errors" — and none of them caught
 * it, so it escaped the seam untyped.
 *
 * Untyped, it is indistinguishable from a genuine programming TypeError, so
 * `handleAsyncDataErrors` could only classify it as "an unknown kind of error"
 * and show a full error page — for what is usually a transient blip. Wrapping
 * it here gives the app one thing to test for, and preserves the original in
 * `cause` so Honeybadger still receives the engine's wording.
 *
 * `statusCode` is `null` because there is no response: a network failure is not
 * a 0 and not a 5xx, and code that switches on status must not treat it as one.
 */
export class NetworkError extends ServiceError {
  method: string
  url: string
  override cause: unknown

  constructor(method: string, url: string, cause: unknown) {
    super(null, `Could not reach the server (${method} ${url}).`)
    this.name = 'NetworkError'
    this.method = method
    this.url = url
    this.cause = cause
  }
}

/**
 * An HTTP 409 from a write — an optimistic-concurrency rejection.
 *
 * Every entity carries a `version`, and the API rejects a write whose submitted
 * version is not the one currently stored. Nothing in the http layer named this:
 * 409 fell through every verb's `default:` to `APIUnhandledStatus`, whose message
 * is only `HTTPStatusText[409]`. That is why a production fault read just
 * "Conflict" — and why the response body, captured into `.error` and never read,
 * could not tell us which constraint or which stored version.
 *
 * `handleAsyncDataErrors` already had the right user-facing message for this, but
 * only under `AsyncOperationError`, i.e. only on the 202-Accepted polling path. A
 * synchronous 409 could never reach it. This type is what makes it reachable.
 *
 * `body` keeps whatever the API sent so the cause survives into Honeybadger.
 */
export class UpdateConflictError extends ServiceError {
  method: string
  url: string
  body: unknown

  constructor(method: string, url: string, body: unknown) {
    super(
      HTTPStatus.Conflict,
      `Update conflict (409) on ${method} ${url} —` +
        ` the submitted version is not the stored one.`
    )
    this.name = 'UpdateConflictError'
    this.method = method
    this.url = url
    this.body = body
  }
}

/**
 * An HTTP 409 from a DELETE — the row is still referenced by something.
 *
 * ⚠ SEPARATE FROM `UpdateConflictError` BECAUSE A DELETE SENDS NO VERSION.
 * `httpDelete` sends `x-request-id`, the auth header and `x-app-version` and
 * nothing else, so "the submitted version is not the stored one" names a cause
 * the request cannot have. It said exactly that in production for a delete
 * blocked by a foreign key (Honeybadger 134111024 / 134110338, 2026-09-04), and
 * `handleAsyncDataErrors` then told the user to refresh and re-apply — advice
 * that cannot work, which they followed three times in 26 minutes.
 *
 * The API's own answer is precise and was already on the wire: `problem_info_for`
 * maps a `DeleteConflict` to a 409 whose `detail` reads
 * `"<entity>=<id> is referenced by a <table> object."`. `parseErrorResponse`
 * lifts that into `detail` here.
 *
 * ⚠ THE REASON GOES IN `.message`, for the reason `ValidationError` below
 * records: Honeybadger's `__buildPayload` emits a fixed key set, so an own
 * `body` property never arrives — which is why the fault title was a generic
 * sentence and the referencing table, the one fact worth having, was dropped in
 * transit. `.message` is also the fault title, so this is what makes these
 * group by which constraint fired.
 */
export class DeleteConflictError extends ServiceError {
  method: string
  url: string
  body: unknown

  constructor(method: string, url: string, detail: string, body: unknown) {
    super(HTTPStatus.Conflict, `${detail} (409 on ${method} ${url})`)
    this.name = 'DeleteConflictError'
    this.method = method
    this.url = url
    this.body = body
  }
}

/**
 * An HTTP 409 from POST /api/cases/{id}/replies — the recipient's address is
 * SUPPRESSED, because a previous message to it bounced or was marked as spam.
 *
 * ⚠ SEPARATE FROM `UpdateConflictError` for the same reason `DeleteConflictError`
 * is: that type's message is "the submitted version is not the stored one", and a
 * reply submits no version at all. Told that, a user is being asked to refresh
 * and re-apply edits that do not exist — the exact advice that failed three times
 * in 26 minutes in faults 134111024 / 134110338.
 *
 * ⚠ THIS ONE IS RECOVERABLE, which is why it is worth typing rather than just
 * relabelling. The server asks BEFORE writing anything, so nothing has happened
 * yet, and `sendToSuppressed` lets the caller proceed knowingly. But proceeding
 * does not deliver: Postmark refuses suppressed addresses at their end, so the
 * message is recorded and then fails, visibly, on the case. The real fix is to
 * reactivate the address in Postmark, or reach the person another way.
 *
 * ⚠ The reason goes in `.message`. Honeybadger's `__buildPayload` emits a fixed
 * key set, so own properties never arrive — the fields below are for the UI,
 * which has the live object; `.message` is what the fault is titled by.
 */
export class SuppressedRecipientError extends ServiceError {
  url: string
  recipient: string
  event: string
  bounceType: string | null
  canReactivate: boolean
  occurredAt: string | null
  body: unknown

  constructor(
    url: string,
    detail: string,
    fields: {
      recipient?: string
      event?: string
      bounceType?: string | null
      canReactivate?: boolean
      occurredAt?: string | null
    },
    body: unknown
  ) {
    super(HTTPStatus.Conflict, `${detail} (409 on POST ${url})`)
    this.name = 'SuppressedRecipientError'
    this.url = url
    this.recipient = fields.recipient ?? ''
    this.event = fields.event ?? ''
    this.bounceType = fields.bounceType ?? null
    // Defaults to FALSE, the pessimistic reading: telling someone an address can
    // be reactivated when it cannot sends them to Postmark for nothing.
    this.canReactivate = fields.canReactivate === true
    this.occurredAt = fields.occurredAt ?? null
    this.body = body
  }
}

/**
 * An HTTP 409 because the CASE was merged into another (cynque-api-server
 * #1658) — from POST /api/cases/{id}/replies on a merged case.
 *
 * ⚠ SEPARATE FROM `UpdateConflictError` for the same reason as the two above:
 * that one's message blames a concurrent edit and says to reload, and a reply
 * sends no version. The case has moved; the user should reply on the survivor,
 * which the server names by id (`mergedIntoId`, parsed from its detail when
 * present).
 *
 * The reason goes in `.message`, which is the fault title and what the save
 * dialog shows.
 */
export class CaseMergedError extends ServiceError {
  url: string
  mergedIntoId: string | null
  body: unknown

  constructor(url: string, mergedIntoId: string | null, body: unknown) {
    super(
      HTTPStatus.Conflict,
      'This case was merged into another case — reply on that case instead' +
        ` (409 on POST ${url})`
    )
    this.name = 'CaseMergedError'
    this.url = url
    this.mergedIntoId = mergedIntoId
    this.body = body
  }
}

/**
 * CaseMergedError for a Service Request, from POST
 * /api/servicerequests/{id}/replies. Forked at `cases-pre-sr-fork`; see
 * CaseMergedError above for why it is its own class.
 */
export class ServiceRequestMergedError extends ServiceError {
  url: string
  mergedIntoId: string | null
  body: unknown

  constructor(url: string, mergedIntoId: string | null, body: unknown) {
    super(
      HTTPStatus.Conflict,
      'This service request was merged into another service request — reply on' +
        ` that service request instead (409 on POST ${url})`
    )
    this.name = 'ServiceRequestMergedError'
    this.url = url
    this.mergedIntoId = mergedIntoId
    this.body = body
  }
}

/**
 * POST /api/cases/{id}/replies refused the reply's ATTACHMENTS
 * (cynque-api-server #1674): a 400 for an empty file or a type the mail
 * provider blocks, or a 413 when everything attached is over its size limit
 * once encoded for email.
 *
 * ⚠ SEPARATE FROM `ValidationError` and `PayloadTooLargeError` on purpose. The
 * save dialog hides a ValidationError's detail (it names endpoints and pydantic
 * keys), and words a PayloadTooLargeError around the 24.5 MB document-upload cap
 * -- the wrong limit for an email. These refusals' `detail` is written for the
 * user and names the files, so it is carried in `.message`, which is what the
 * dialog shows. `filenames` is the server's `extra.filenames`, when it sends one.
 */
export class ReplyAttachmentsRefusedError extends ServiceError {
  url: string
  filenames: string[]
  body: unknown

  constructor(
    statusCode: number,
    url: string,
    detail: string,
    filenames: string[],
    body: unknown
  ) {
    super(statusCode, `${detail} (${statusCode} on POST ${url})`)
    this.name = 'ReplyAttachmentsRefusedError'
    this.url = url
    this.filenames = filenames
    this.body = body
  }
}

/**
 * An HTTP 400 or 422 from a write — the API rejected the payload itself.
 *
 * Modelled on `UpdateConflictError` above, for the same reason: without a type,
 * these fell through as a plain `ServiceError` whose message was the parsed body
 * coerced to the literal string '[object Object]' (see `describeErrorBody` in
 * services/http/utils.ts). Faults 133565364 / 133565262, 2026-08-15.
 *
 * 400 and 422 share ONE class deliberately. Litestar raises `ValidationException`
 * for both, differing only in where the failure occurred; to the user and to the
 * person fixing it they are the same event.
 *
 * ⚠ The reason lives in `.message`, not only in `.body`. Honeybadger's
 * `__buildPayload` emits a fixed key set, so a custom `body` property is dropped
 * in transit — which means `UpdateConflictError.body` has never reached it
 * either. `.message` is also the fault title, so putting the reason there is
 * what makes these group by cause instead of into one bucket.
 */
export class ValidationError extends ServiceError {
  method: string
  url: string
  body: unknown

  constructor(
    statusCode: number,
    method: string,
    url: string,
    detail: string,
    body: unknown
  ) {
    super(statusCode, `${detail} (${statusCode} on ${method} ${url})`)
    this.name = 'ValidationError'
    this.method = method
    this.url = url
    this.body = body
  }
}

/**
 * An HTTP 413 from a write — the request body exceeded the API's cap.
 *
 * Modelled on `ValidationError` above, for the same reason: untyped, a 413 fell
 * through `default:` to `APIUnhandledStatus`, whose message is only
 * `HTTPStatusText[413]`. That is why a production fault read just "Payload Too
 * Large" and the user was shown "Couldn't create Document (Payload Too Large)" —
 * no limit, no filename, no hint that a smaller file would work. Faults
 * 133922044 / 133922043, 2026-08-29.
 *
 * ⚠ `.message` names the LIMIT but deliberately NOT the file. It is the
 * Honeybadger fault title, so it is also what makes these group as one issue
 * instead of fragmenting into a separate fault per filename and per byte count.
 * `fileName` / `fileSize` carry the specifics for the notification builder in
 * cynqueModules/componentHelpers/entityDialog/saveFlow.ts — they are NOT
 * transmitted to Honeybadger, which emits only a fixed key set.
 *
 * A 413 should now be rare: `MAX_UPLOAD_FILE_BYTES` is enforced at pick time by
 * the `maxBytes` rule. Reaching here means the client cap has drifted above the
 * API's, which is exactly the drift worth being told about.
 */
export class PayloadTooLargeError extends ServiceError {
  method: string
  url: string
  fileName?: string
  fileSize?: number
  maxBytes: number
  body: unknown

  constructor(
    method: string,
    url: string,
    maxBytes: number,
    file: { name: string; size: number } | undefined,
    body: unknown
  ) {
    super(
      HTTPStatus.PayloadTooLarge,
      `Upload exceeds the ${formatBytes(maxBytes)} limit` +
        ` (${HTTPStatus.PayloadTooLarge} on ${method} ${url})`
    )
    this.name = 'PayloadTooLargeError'
    this.method = method
    this.url = url
    this.fileName = file?.name
    this.fileSize = file?.size
    this.maxBytes = maxBytes
    this.body = body
  }
}

export class APIUnhandledStatus extends ServiceError implements IServiceError {
  error: IServiceError

  constructor(statusCode: number, error: IServiceError) {
    super(statusCode, HTTPStatusText[statusCode as HTTPStatusCode])
    this.error = Object.assign({}, error)
  }
}

/**
 * The Opportunity Overview's OPERATIONS tab was opened for an opportunity that
 * has no order.
 *
 * ⚠ NOT AN API ERROR — the request succeeded and returned nothing, which is a
 * legitimate answer (`/api/opportunities/:id/orderoverview` returns null for an
 * opportunity nobody has signed a contract on). It is modelled as an error only
 * because the silo factory's `asyncData` has no other way to say "do not render
 * this page"; `handleAsyncDataErrors` turns it into a redirect to the Sales tab
 * rather than an error page.
 *
 * It is reachable without any bug: the tab is hidden when there is no order, but
 * a stored `selectedScreenTopTabValue: 'Operations'` (or a bookmarked URL, or an
 * order deleted in another tab) still routes here.
 *
 * `statusCode` is deliberately null — giving it 404 would send it down the
 * isIServiceError 404 branch, which redirects to the entity LIST page.
 */
export class NoOrderForOpportunityError extends ServiceError {
  opportunityId: string

  constructor(opportunityId: string) {
    super(null, `Opportunity '${opportunityId}' has no order.`)
    this.name = 'NoOrderForOpportunityError'
    this.opportunityId = opportunityId
  }
}
